import { Actions, createStore } from '@bpd-library/utilities';
import { AreaSubscriptionState, AreaSubscriptionStatus } from '@features/area-subscription/types';
import { getCurrentSubscriptionState } from '@features/area-subscription/utilities';

type AreaSubscriptionStoreAction =
    | 'setState'
    | 'setStatus'
    | 'setAreaOnly'
    | 'setAreaSelected'
    | 'setFormRequired'
    | 'toggleProjectPhaseSelected'
    | 'clearSelectedProjectPhases'
    | 'togglePropertyTypeSelected'
    | 'clearSelectedPropertyTypes'
    | 'setSubmitting'
    | 'resetState';

type AreaSubscriptionToggleData = {
    id: string;
    selected: boolean;
};

const initialState: AreaSubscriptionState = {
    formRequired: false,
    submitting: false,
    selectedProjectPhases: [],
    selectedPropertyTypes: [],
};

const actions: Actions<AreaSubscriptionStoreAction, AreaSubscriptionState> = {
    setState: (state: AreaSubscriptionState, payload: Partial<AreaSubscriptionState>) => {
        state.status = payload.status ?? state.status;
        state.areaOnly = payload.areaOnly ?? state.areaOnly;
        state.formRequired = payload.formRequired ?? state.formRequired;
        state.areaSelected = payload.areaSelected ?? state.areaSelected;
        state.submitting = payload.submitting ?? state.submitting;
        state.selectedProjectPhases = payload.selectedProjectPhases ?? state.selectedProjectPhases;
        state.selectedPropertyTypes = payload.selectedPropertyTypes ?? state.selectedPropertyTypes;
        return state;
    },
    setStatus: (state: AreaSubscriptionState, status: AreaSubscriptionStatus) => {
        state.status = status;
        return state;
    },
    setAreaOnly: (state: AreaSubscriptionState, areaOnly: boolean) => {
        state.areaOnly = areaOnly;
        return state;
    },
    setAreaSelected: (state: AreaSubscriptionState, areaSelected: boolean) => {
        state.areaSelected = areaSelected;
        return state;
    },
    setFormRequired: (state: AreaSubscriptionState, formRequired: boolean) => {
        state.formRequired = formRequired;
        return state;
    },
    toggleProjectPhaseSelected: (
        state: AreaSubscriptionState,
        { id, selected }: AreaSubscriptionToggleData,
    ) => {
        if (selected) {
            state.selectedProjectPhases = [...state.selectedProjectPhases, id];
        } else {
            state.selectedProjectPhases = state.selectedProjectPhases.filter((i) => i !== id);
        }
        return state;
    },
    clearSelectedProjectPhases: (state: AreaSubscriptionState) => {
        state.selectedProjectPhases = [];
        return state;
    },
    togglePropertyTypeSelected: (
        state: AreaSubscriptionState,
        { id, selected }: AreaSubscriptionToggleData,
    ) => {
        if (selected) {
            state.selectedPropertyTypes = [...state.selectedPropertyTypes, id];
        } else {
            state.selectedPropertyTypes = state.selectedPropertyTypes.filter((i) => i !== id);
        }
        return state;
    },
    clearSelectedPropertyTypes: (state: AreaSubscriptionState) => {
        state.selectedPropertyTypes = [];
        return state;
    },
    setSubmitting: (state: AreaSubscriptionState, submitting: boolean) => {
        state.submitting = submitting;
        return state;
    },
    resetState: (state: AreaSubscriptionState) => {
        state.submitting = false;
        state.selectedProjectPhases = [];
        state.selectedPropertyTypes = [];
        return state;
    },
};

export const areaSubscriptionStore = createStore<
    AreaSubscriptionState,
    AreaSubscriptionStoreAction
>({
    initialState,
    actions,
});

export const updateAreaSubscriptionState = async () => {
    const extendedState = await getCurrentSubscriptionState();
    const { availableProjectIds, subscribedProjectIds, ...state } = extendedState;
    setAreaSubscriptionState(state);
    return extendedState;
};

export const setAreaSubscriptionState = (state: Partial<AreaSubscriptionState>) => {
    areaSubscriptionStore.dispatch('setState', state);
};

export const setAreaSubscriptionAreaOnly = (areaOnly: boolean) => {
    areaSubscriptionStore.dispatch('setAreaOnly', areaOnly);
};

export const setAreaSubscriptionFormRequired = (required: boolean) => {
    areaSubscriptionStore.dispatch('setFormRequired', required);
};

export const toggleProjectPhaseSelected = (id: string, selected: boolean) => {
    areaSubscriptionStore.dispatch('toggleProjectPhaseSelected', { id, selected });
};

export const clearSelectedProjectPhases = () => {
    areaSubscriptionStore.dispatch('clearSelectedProjectPhases');
};

export const togglePropertyTypeSelected = (id: string, selected: boolean) => {
    areaSubscriptionStore.dispatch('togglePropertyTypeSelected', { id, selected });
};

export const clearSelectedPropertyTypes = () => {
    areaSubscriptionStore.dispatch('clearSelectedPropertyTypes');
};

export const setAreaSubscriptionFormSubmitting = (submitting: boolean) => {
    areaSubscriptionStore.dispatch('setSubmitting', submitting);
};

export const resetAreaSubscriptionState = () => {
    areaSubscriptionStore.dispatch('resetState');
};
